import { NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHConflictOfInterestBannerView } from "../../Custom components/RH/RHConflictOfInterestBannerView"
import { RHTeamUserReferenceResolver } from "../../Custom components/RH/RHTeamRoleSectionView"
import { RHColors, RHFont, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import {
    RHTeamRoleMatrixRole,
    RHTeamRoleMatrixView
} from "../../NewProcurement/ProcurementTeamView"
import { RHOfferCapabilities, RHOfferTeam } from "../../SocketClientFunctions"


export class OfferTeamView extends UIView {

    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Pakkuja meeskond",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textPrimary
        })
        .addedAsSubviewToView(this)

    readonly explanationLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Pakkumuse rollid kehtivad ainult selle pakkumuse piires. Vastutav ja volitatud isikud saavad pakkumust koostada ning esitada.",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textMuted,
            isSingleLine: NO,
            style: {
                fontSize: RHFont.sizeSmall,
                overflowWrap: "anywhere",
                whiteSpace: "normal"
            }
        })
        .addedAsSubviewToView(this)

    readonly statusBanner = new RHAlertBannerView()

    readonly conflictOfInterestBannerView = new RHConflictOfInterestBannerView()

    readonly roleMatrixView = new RHTeamRoleMatrixView()
        .addedAsSubviewToView(this) as RHTeamRoleMatrixView

    teamDidChange?: (capabilities?: RHOfferCapabilities) => Promise<void> | void

    _offerId = ""
    _procurementId = ""
    _team?: RHOfferTeam
    _capabilities?: RHOfferCapabilities
    _isApplyingRoleMatrixChange = NO

    override _focusManager = new RHFocusManager(() => [this.roleMatrixView._focusManager])

    constructor(elementID?: string) {
        super(elementID)
        this.roleMatrixView.addsCurrentUserWhenMissing = NO
        this.roleMatrixView.currentUserSelfAdditionCalloutView.bodyText =
            "Pakkumuse sisu ei kuvata enne, kui oled end pakkuja meeskonda volitatud isikuna lisanud."
        this._configureRoleMatrixPresentation([])
        this.roleMatrixView.teamDidChange = () => {
            void this._applyRoleMatrixChange()
        }

        this.roleMatrixView.currentUserSelfAdditionWasRequested = role => {
            if (
                role !== "authorized" ||
                !this._canCurrentUserAddSelfToOfferTeam()
            ) {
                return
            }
            this._presentCurrentUserTeamSelfAdditionConfirmation()
        }
        this._reloadActionViews()
    }

    async load(offerId: string, procurementId?: string) {
        this._offerId = offerId
        if (procurementId) {
            this._procurementId = procurementId
        }
        this.loading = YES

        try {
            const team = await RHCore.sharedInstance.offerStore.retrieveOfferTeam(offerId)
            this._team = team
            this._capabilities = team.capabilities
            const offererCompanyId = RHTeamUserReferenceResolver.userIdFromReference(team.offererCompany)
            await this.roleMatrixView.loadCompanyUsers(offererCompanyId)
            if (this._procurementId.length > 0) {
                await this.conflictOfInterestBannerView.load({
                    procurementId: this._procurementId,
                    fromSide: "offer",
                    offerId
                })
            }
            else {
                this.conflictOfInterestBannerView.clear()
            }
            this._reloadRoleMatrix()
            this._reloadActionViews()
        }
        catch (error) {
            console.error("Offer team load error:", error)
            this.statusBanner.configuredWithObject({
                alertType: RHAlertBannerView.type.Error,
                text: "Pakkuja meeskonna laadimine ebaõnnestus."
            })
            this.statusBanner.addedAsSubviewToView(this)
        }
        finally {
            this.loading = NO
            this.setNeedsLayoutUpToRootView()
        }
    }

    get capabilities(): RHOfferCapabilities | undefined {
        return this._capabilities
    }

    _configureRoleMatrixPresentation(editableRoles: RHTeamRoleMatrixRole[]) {
        this.roleMatrixView.configureRoleMatrixPresentation({
            visibleRoles: ["responsible", "authorized"],
            editableRoles,
            roleDescriptions: {
                responsible: "Vastutav isik saab pakkumust koostada ja esitada ning vastutava rolli üle anda.",
                authorized: "Volitatud isik saab pakkumust koostada ja esitada."
            },
            canCurrentUserChangeOwnRole: NO,
            shouldShowHeaderContent: NO,
            shouldShowCompanyMemberInvitationNotice: NO
        })
    }

    _reloadRoleMatrix() {
        const editableRoles: RHTeamRoleMatrixRole[] = []
        if (this._capabilities?.canChangeResponsibleUser === YES) {
            editableRoles.push("responsible")
        }
        if (this._capabilities?.canManageAuthorizedUsers === YES) {
            editableRoles.push("authorized")
        }
        this._configureRoleMatrixPresentation(editableRoles)
        this.roleMatrixView.configureRoleAssignments({
            responsibleUser: this._team?.responsibleOffererUser,
            authorizedUsers: this._team?.authorizedOffererUsers,
            observerUsers: this._team?.observerOffererUsers
        })
        this.roleMatrixView.canCurrentUserAddSelfToTeam =
            this._canCurrentUserAddSelfToOfferTeam()
        this.roleMatrixView.configureHistoryEntries(this._team?.history)
    }

    _canCurrentUserAddSelfToOfferTeam(): boolean {
        if (
            this._capabilities?.role !== "administrator" ||
            this._capabilities.canAddSelfToTeam !== YES
        ) {
            return NO
        }

        const activeCompanyAffiliation = RHCore.sharedInstance.companyAffiliationStore
            .activeCompanyAffiliation
        const activeCompanyId = RHTeamUserReferenceResolver.userIdFromReference(
            activeCompanyAffiliation?.company
        )
        const offererCompanyId = RHTeamUserReferenceResolver.userIdFromReference(
            this._team?.offererCompany
        )
        return offererCompanyId.length > 0 &&
            activeCompanyId === offererCompanyId &&
            activeCompanyAffiliation?.source !== "relationship" &&
            activeCompanyAffiliation?.membershipStatus === "active" &&
            activeCompanyAffiliation.isAuditor !== YES &&
            activeCompanyAffiliation.isAdministrator === YES
    }

    _reloadActionViews() {
        this.statusBanner.removeFromSuperview()
        this.conflictOfInterestBannerView.removeFromSuperview()
        if (this.conflictOfInterestBannerView.isVisible) {
            this.conflictOfInterestBannerView.addedAsSubviewToView(this)
        }

        if (this._capabilities?.role === "auditor") {
            this.statusBanner.configuredWithObject({
                alertType: RHAlertBannerView.type.Info,
                text: "Selles vaates saab pakkumuse andmeid ainult lugeda. Meeskonna muutmise toimingud ei ole saadaval."
            })
            this.statusBanner.addedAsSubviewToView(this)
        }
    }

    async _applyRoleMatrixChange() {
        if (!this._team || this._offerId.length === 0) {
            return
        }
        if (this._isApplyingRoleMatrixChange === YES) {
            this._reloadRoleMatrix()
            return
        }

        const previousTeam = this._team
        const previousResponsibleUserId = RHTeamUserReferenceResolver.userIdFromReference(
            previousTeam.responsibleOffererUser
        )
        const responsibleUserId = this.roleMatrixView.responsibleUserId
        if (responsibleUserId.length === 0) {
            CBDialogViewShower.alert("Vastutavaks isikuks tuleb valida täpselt üks kasutaja.")
            this._reloadRoleMatrix()
            return
        }

        try {
            this._isApplyingRoleMatrixChange = YES
            this.roleMatrixView.loading = YES
            if (responsibleUserId !== previousResponsibleUserId) {
                this._team = await RHCore.sharedInstance.offerStore.changeOfferResponsibleUser(
                    this._offerId,
                    responsibleUserId
                )
            }
            else {
                this._team = await RHCore.sharedInstance.offerStore.updateOfferTeam(
                    this._offerId,
                    {
                        authorizedOffererUserIds: this.roleMatrixView.authorizedUserIds,
                        observerOffererUserIds: this.roleMatrixView.observerUserIds
                    }
                )
            }
            this._capabilities = this._team.capabilities
            this._reloadRoleMatrix()
            this._reloadActionViews()
            await this.teamDidChange?.(this._capabilities)
        }
        catch (error) {
            console.error("Offer team update error:", error)
            this._team = previousTeam
            this._capabilities = previousTeam.capabilities
            this._reloadRoleMatrix()
            let errorMessage = "Pakkuja meeskonna uuendamine ebaõnnestus."
            if (error instanceof Error && error.message) {
                errorMessage = error.message
            }
            CBDialogViewShower.alert(errorMessage)
        }
        finally {
            this.roleMatrixView.loading = NO
            this._isApplyingRoleMatrixChange = NO
            this.setNeedsLayoutUpToRootView()
        }
    }

    _presentCurrentUserTeamSelfAdditionConfirmation() {
        const shower = CBDialogViewShower.showQuestionDialog(
            "Kinnitan huvide konflikti puudumise"
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Kinnitan"
        shower.dialogView.view.noButton.titleLabel.text = "Tühista"
        shower.dialogView.view.setNeedsLayoutUpToRootView()

        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            await this._addCurrentUserToTeam()
        }
    }

    async _addCurrentUserToTeam() {
        if (
            this._offerId.length === 0 ||
            !this._canCurrentUserAddSelfToOfferTeam()
        ) {
            return
        }

        try {
            this.loading = YES
            this._team = await RHCore.sharedInstance.offerStore.addCurrentUserToOfferTeam(this._offerId)
            this._capabilities = this._team.capabilities
            this._reloadRoleMatrix()
            this._reloadActionViews()
            await this.teamDidChange?.(this._capabilities)
        }
        catch (error) {
            console.error("Add current user to offer team error:", error)
            let errorMessage = "Pakkuja meeskonnaga liitumine ebaõnnestus."
            if (error instanceof Error && error.message) {
                errorMessage = error.message
            }
            CBDialogViewShower.alert(errorMessage)
        }
        finally {
            this.loading = NO
            this.setNeedsLayoutUpToRootView()
        }
    }

    _visibleFlowViews(): UIView[] {
        const views: UIView[] = [this.titleLabel, this.explanationLabel]
        if (this.statusBanner.superview === this) {
            views.push(this.statusBanner)
        }
        if (this.conflictOfInterestBannerView.superview === this) {
            views.push(this.conflictOfInterestBannerView)
        }
        views.push(this.roleMatrixView)
        return views
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const padding = RHSpacing.small
        const bounds = this.contentBoundsWithInset(padding)
        const views = this._visibleFlowViews()

        bounds.framesByDistributingViewsAsColumn(
            views,
            RHSpacing.small,
            views
        )
    }

}
